/**
 * Stored settings, read against the registry: what a knob is on, and what happens when the save
 * disagrees with this build.
 *
 * A settings section never refuses a load. That is the one place this differs from every other
 * section of a save, and it is deliberate: gear a build cannot name is a character losing something
 * irreplaceable, while a sensitivity a build cannot name is a slider that goes back to the middle.
 * Refusing a whole profile over a knob would cost the player far more than the knob is worth.
 *
 * So there are three answers and no fourth. A value the control can use is used, a value out of
 * range is pulled back into range by the control that would have drawn it, and anything else falls
 * back to the knob's default.
 *
 * A key this build does not know is kept, never dropped. Yesterday's build wrote it, tomorrow's
 * build may want it back, and a write that silently deleted it would make downgrading a game a way
 * to lose settings.
 */

import { controlFor } from '@/ui/settings/settingControlRegistry';
import type { SettingDefinition, SettingValue } from '@/ui/settings/settingContract';
import type { StoredSettings } from '@/save/saveContract';

/**
 * A raw value made usable: in range if it can be, the knob's default if it cannot.
 *
 * The one place that decision is made. A value off disk and a value off a slider go through the
 * same call, so what is stored, what is applied and what is drawn cannot disagree.
 */
export function coerceSetting(definition: SettingDefinition, raw: unknown): SettingValue {
  return (
    controlFor(definition.control.kind).coerce(definition.control, raw) ?? definition.defaultValue
  );
}

/** What this knob is on, given whatever the save carried. Never throws and never refuses. */
export function settingValue(definition: SettingDefinition, stored: StoredSettings): SettingValue {
  const raw = Object.hasOwn(stored, definition.id) ? stored[definition.id] : undefined;
  return coerceSetting(definition, raw);
}

/**
 * Puts every knob into effect.
 *
 * Called once when a profile loads and again when an import replaces it, because the character is
 * where settings live and a different character can have different ones.
 */
export function applySettings(
  stored: StoredSettings,
  definitions: readonly SettingDefinition[],
): void {
  for (const definition of definitions) definition.apply(settingValue(definition, stored));
}

/**
 * The stored record with one knob changed.
 *
 * The value is put through the same coercion a stored one goes through, so a caller cannot write
 * something the next load would refuse to read back.
 */
export function withSetting(
  stored: StoredSettings,
  definition: SettingDefinition,
  value: SettingValue,
): StoredSettings {
  return { ...stored, [definition.id]: coerceSetting(definition, value) };
}
